import { useEffect, useRef } from "react";

/**
 * The canvas's `componentDidMount` motion runtime, as a hook.
 *
 * Scoped to a root element rather than `document`. The canvas runs alone in an
 * iframe and can query globally; here the page is one route inside an SPA, so a
 * global `querySelectorAll('section')` would reach into whatever else is
 * mounted. Every lookup below is rooted.
 *
 * Two behaviours in the canvas source are deliberately NOT ported, because they
 * are dead there and porting them would carry the corpse rather than the design:
 *
 *   - The cursor-glow and mouse-tilt blocks query `[data-glow]`, `[data-scene]`,
 *     `[data-tilt]` and `[data-depth]`. None of those attributes exist anywhere
 *     in the canvas markup — the author's own note ("hero is static now") marks
 *     when they stopped being emitted. Both blocks are unreachable, and the
 *     `cursorGlow` prop that gates one of them is therefore inert.
 *   - `data-rv-d` appears on 16 elements and is read by nothing. Reveal delay
 *     comes from sibling index alone. Honouring the attribute would make this
 *     port animate differently from the canvas it is copying.
 */

// The canonical strong ease-out, matching --ease-out in styles.ts. This was
// cubic-bezier(.16,1,.3,1), one of three near-identical curves the canvas
// had hand-typed; they now all resolve to the same one.
const EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

export interface DcMotionOptions {
  /** 0-2. The canvas ships 2. Live-read, so a change takes effect next frame. */
  parallaxIntensity: number;
}

export function useDcMotion(
  rootRef: React.RefObject<HTMLElement>,
  { parallaxIntensity }: DcMotionOptions,
): void {
  // Read inside the rAF loop so intensity can change without re-registering
  // scroll listeners or re-measuring the document.
  const intensityRef = useRef(parallaxIntensity);
  intensityRef.current = parallaxIntensity;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const cleanup: Array<() => void> = [];
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const desktop = window.matchMedia("(min-width: 768px)");

    // --- Scroll reveals: once only, 80ms sibling stagger, capped at 5 -------
    const rvs = Array.from(root.querySelectorAll<HTMLElement>("[data-rv]"));
    if (reduce) {
      rvs.forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "none";
      });
    } else {
      rvs.forEach((el) => {
        const parent = el.parentNode as HTMLElement | null;
        const sibs = parent
          ? Array.from(parent.children).filter((c) => c.hasAttribute("data-rv"))
          : [el];
        const i = Math.max(0, sibs.indexOf(el));
        el.style.opacity = "0";
        // data-rv-z pushes the card back in Z so it arrives through the
        // section's `perspective`, rather than merely sliding up.
        el.style.transform = el.hasAttribute("data-rv-z")
          ? "translate3d(0,16px,-60px)"
          : "translate3d(0,16px,0)";
        el.style.transition = `opacity 500ms ${EASE}, transform 500ms ${EASE}`;
        el.style.transitionDelay = `${Math.min(i, 5) * 80}ms`;
      });
      const io = new IntersectionObserver(
        (es) =>
          es.forEach((e) => {
            if (!e.isIntersecting) return;
            const el = e.target as HTMLElement;
            el.style.willChange = "transform, opacity";
            el.style.opacity = "1";
            el.style.transform = "translate3d(0,0,0)";
            // Dropping to `transform: none` releases the compositor layer once
            // the element has landed. This is also why the hover rules in
            // styles.ts need !important - this inline value outranks a class.
            el.addEventListener(
              "transitionend",
              () => {
                el.style.willChange = "";
                el.style.transform = "none";
              },
              { once: true },
            );
            io.unobserve(el);
          }),
        { threshold: 0.15 },
      );
      rvs.forEach((el) => io.observe(el));
      cleanup.push(() => io.disconnect());
    }

    // --- Dashboard chart bars grow when the chart scrolls in ---------------
    const chart = root.querySelector<HTMLElement>("[data-chart]");
    if (chart && !reduce) {
      const bars = Array.from(chart.querySelectorAll<HTMLElement>("[data-bar]"));
      bars.forEach((b) => {
        b.style.transform = "scaleY(.05)";
        b.style.transformOrigin = "bottom";
        b.style.transition = "transform 1s cubic-bezier(.2,.8,.2,1)";
      });
      const iob = new IntersectionObserver(
        (es) =>
          es.forEach((e) => {
            if (!e.isIntersecting) return;
            bars.forEach((b, i) => {
              b.style.transitionDelay = `${200 + i * 110}ms`;
              b.style.transform = "scaleY(1)";
            });
            iob.unobserve(e.target);
          }),
        { threshold: 0.4 },
      );
      iob.observe(chart);
      cleanup.push(() => iob.disconnect());
    }

    // --- Parallax: one rAF loop, lerp smoothed, reads batched before writes -
    interface PlxLayer {
      el: HTMLElement;
      s: number;
      cap: number;
      direct: boolean;
      base: number;
      h: number;
      cur: number;
      tgt: number;
    }
    const plxEls: PlxLayer[] = Array.from(
      root.querySelectorAll<HTMLElement>("[data-plx]"),
    ).map((el) => ({
      el,
      s: parseFloat(el.getAttribute("data-plx") || "") || 0,
      cap: parseFloat(el.getAttribute("data-plx-max") || "") || 120,
      direct: el.hasAttribute("data-plx-direct"),
      base: 0,
      h: 0,
      cur: 0,
      tgt: 0,
    }));

    // Measured once per resize, never per frame. `- o.cur` backs out the
    // transform already applied, so re-measuring mid-scroll stays stable.
    const measure = () => {
      plxEls.forEach((o) => {
        const r = o.el.getBoundingClientRect();
        o.base = r.top + window.scrollY - o.cur;
        o.h = r.height;
      });
    };
    measure();

    let raf = 0;
    const frame = () => {
      raf = 0;
      const vh = window.innerHeight;
      const sy = window.scrollY;
      const k = reduce || !desktop.matches ? 0 : intensityRef.current;
      let moving = false;
      for (let i = 0; i < plxEls.length; i++) {
        const o = plxEls[i];
        const center = o.base + o.h / 2 - sy;
        let t = 0;
        if (k && center > -o.h - 400 && center < vh + o.h + 400) {
          t = (vh / 2 - center) * o.s * k * -1;
          t = Math.max(-o.cap, Math.min(o.cap, t));
        }
        o.tgt = t;
        // The hero video tracks scroll with no easing; a lerped video layer
        // visibly lags the frame it is composited into.
        if (o.direct) {
          if (o.cur !== o.tgt) {
            o.cur = o.tgt;
            o.el.style.transform = `translate3d(0,${o.cur.toFixed(2)}px,0)`;
          }
          continue;
        }
        const d = o.tgt - o.cur;
        if (Math.abs(d) < 0.08) {
          o.cur = o.tgt;
        } else {
          o.cur += d * 0.08;
          moving = true;
        }
        o.el.style.transform =
          o.cur === 0 ? "" : `translate3d(0,${o.cur.toFixed(2)}px,0)`;
        o.el.style.willChange = o.cur === 0 ? "" : "transform";
      }
      if (moving) raf = requestAnimationFrame(frame);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const onResize = () => {
      measure();
      onScroll();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    frame();
    cleanup.push(() => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    });

    // --- Pause ambient animations while their section is offscreen ---------
    const sections = Array.from(
      root.querySelectorAll<HTMLElement>("header#hero, section, footer"),
    );
    const animMap = new Map<HTMLElement, HTMLElement[]>();
    sections.forEach((section) => {
      const els = Array.from(section.querySelectorAll<HTMLElement>("*")).filter(
        (el) => {
          const cs = getComputedStyle(el);
          return (
            cs.animationName !== "none" &&
            cs.animationIterationCount.includes("infinite")
          );
        },
      );
      if (els.length) animMap.set(section, els);
    });
    if (animMap.size) {
      const aio = new IntersectionObserver(
        (es) =>
          es.forEach((e) => {
            const els = animMap.get(e.target as HTMLElement);
            if (!els) return;
            els.forEach((el) => {
              el.style.animationPlayState = e.isIntersecting
                ? "running"
                : "paused";
            });
          }),
        { rootMargin: "120px" },
      );
      animMap.forEach((_, section) => aio.observe(section));
      cleanup.push(() => aio.disconnect());
    }

    return () => cleanup.forEach((fn) => fn());
    // Runs once on mount, exactly as componentDidMount does. Intensity changes
    // are picked up through intensityRef rather than by re-running this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * The canvas's `_syncVideo` / `componentDidUpdate` pair.
 *
 * Dark mode does not merely hide the hero video, it tears the source out and
 * calls `load()` - a hidden 4K video otherwise keeps a decoder running for a
 * layer at `opacity: 0`. The src is stashed so returning to light mode can
 * restore it without another round trip through the component tree.
 */
export function useHeroVideo(
  videoRef: React.RefObject<HTMLVideoElement>,
  heroRef: React.RefObject<HTMLElement>,
  dark: boolean,
): void {
  const visibleRef = useRef(true);
  const storedSrcRef = useRef<string | null>(null);
  const darkRef = useRef(dark);
  darkRef.current = dark;

  const sync = useRef<() => void>(() => {});
  sync.current = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (darkRef.current || !visibleRef.current) {
      vid.pause();
      if (darkRef.current && storedSrcRef.current === null) {
        storedSrcRef.current = vid.src;
        vid.removeAttribute("src");
        vid.load();
      }
    } else {
      if (storedSrcRef.current !== null) {
        vid.src = storedSrcRef.current;
        storedSrcRef.current = null;
        vid.load();
      }
      if (vid.paused) {
        // play() predates promises and still returns undefined in some engines
        // (and in jsdom), so the result is guarded before it is awaited — the
        // canvas does the same. Autoplay can also be refused outright (low
        // power mode, a user setting); the page reads correctly with a paused
        // first frame, so that rejection is swallowed.
        const p = vid.play();
        if (p) void p.catch(() => {});
      }
    }
  };

  useEffect(() => {
    const hero = heroRef.current;
    if (!videoRef.current || !hero) return;
    const vio = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          visibleRef.current = e.isIntersecting;
          sync.current();
        }),
      { rootMargin: "80px" },
    );
    vio.observe(hero);
    sync.current();
    return () => vio.disconnect();
  }, [heroRef, videoRef]);

  // Mirrors componentDidUpdate: any theme change re-runs the sync.
  useEffect(() => {
    sync.current();
  }, [dark]);
}
